import { type DocumentData, type DocumentSnapshot, doc } from 'firebase/firestore'

import { DEFAULT_LOCALE, isSupportedLocale } from '@/i18n'
import { callable } from '@/lib/firebase/callable'
import { createReasonGuard } from '@/lib/firebase/errors'
import { type Subscription, subscribeDoc } from '@/lib/firebase/subscribe'
import {
  type SetupScope,
  asIsoOrNull,
  asNumber,
  asOneOf,
  asRecords,
  asString,
  asStringOrNull,
  isRecord,
  setupCollection,
  subscribeLatestRun,
  toRunEntry,
  toSetupRun,
  toBrandDocMeta,
  toSources,
} from '@/repositories/setup-docs'
import {
  type BrandFacts,
  type BrandFactsPage,
  type BrandFactsPageEntry,
  type BrandFactsRun,
  BRAND_FACTS_RUN_STAGES,
  type BrandKind,
  type FactPerson,
  type PeopleFact,
  type RegionArea,
  type RegionCountry,
  type RegionFact,
  type TextFact,
} from '@/types/brand-facts'
import { FACT_BASES } from '@/types/setup-source'

const BRAND_KINDS: readonly BrandKind[] = ['product', 'service', 'both']

function toTextFact(value: unknown): TextFact {
  const fact = isRecord(value) ? value : {}
  return {
    value: typeof fact.value === 'string' && fact.value !== '' ? fact.value : null,
    sources: toSources(fact.sources),
    basis: asOneOf(fact.basis, FACT_BASES, 'empty'),
  }
}

function toRegionFact(value: unknown): RegionFact {
  const fact = isRecord(value) ? value : {}
  const countries: RegionCountry[] = asRecords(fact.countries)
    .map((entry) => ({ code: asString(entry.code), sources: toSources(entry.sources) }))
    .filter((entry) => entry.code !== '')
  const regions: RegionArea[] = asRecords(fact.regions)
    .map((entry) => ({ code: asString(entry.code), sources: toSources(entry.sources) }))
    .filter((entry) => entry.code !== '')
  return { countries, regions, basis: asOneOf(fact.basis, FACT_BASES, 'empty') }
}

function toPeopleFact(value: unknown): PeopleFact {
  const fact = isRecord(value) ? value : {}
  const people: FactPerson[] = asRecords(fact.people)
    .map((entry) => ({
      name: asString(entry.name),
      role: asString(entry.role),
      sources: toSources(entry.sources),
    }))
    .filter((entry) => entry.name !== '')
  return { people, basis: asOneOf(fact.basis, FACT_BASES, 'empty') }
}

function toPages(value: unknown): BrandFactsPage[] {
  return asRecords(value)
    .map((page) => ({ url: asString(page.url), title: asString(page.title) }))
    .filter((page) => page.url !== '')
}

function toBrandFacts(data: DocumentData): BrandFacts {
  return {
    meta: toBrandDocMeta(data.meta),
    websiteUrl: asString(data.websiteUrl),
    linkedInUrl: asString(data.linkedInUrl),
    brandInCategory: toTextFact(data.brandInCategory),
    category: toTextFact(data.category),
    problemItSolves: toTextFact(data.problemItSolves),
    targetCustomer: toTextFact(data.targetCustomer),
    categoryDefinition: toTextFact(data.categoryDefinition),
    approach: toTextFact(data.approach),
    region: toRegionFact(data.region),
    people: toPeopleFact(data.people),
    kind: typeof data.kind === 'string' ? asOneOf(data.kind, BRAND_KINDS, 'product') : null,
    language: isSupportedLocale(data.language) ? data.language : DEFAULT_LOCALE,
    readAt: asIsoOrNull(data.readAt),
    pages: toPages(data.pages),
    runId: asString(data.runId),
  }
}

function toPageEntry(entry: DocumentData): BrandFactsPageEntry {
  return {
    ...toRunEntry(entry),
    url: asString(entry.url),
    ...(typeof entry.title === 'string' ? { title: entry.title } : {}),
  }
}

function toBrandFactsRun(snapshot: DocumentSnapshot<DocumentData>): BrandFactsRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    ...toSetupRun(snapshot.id, data, BRAND_FACTS_RUN_STAGES, toPageEntry),
    websiteUrl: asString(data.websiteUrl),
    linkedInUrl: asString(data.linkedInUrl),
    keepEdited: data.keepEdited === true,
    fieldsFilled: asNumber(data.fieldsFilled),
    websiteReadRunId: asStringOrNull(data.websiteReadRunId),
  }
}

export function subscribeBrandFacts(scope: SetupScope): Subscription<BrandFacts> {
  return subscribeDoc(doc(setupCollection(scope, 'brandFacts'), 'current'), (snapshot) => {
    const data = snapshot.data()
    return data ? toBrandFacts(data) : undefined
  })
}

export function subscribeLatestBrandFactsRun(
  scope: SetupScope,
): Subscription<BrandFactsRun | null> {
  return subscribeLatestRun(scope, 'brandFactsRuns', toBrandFactsRun)
}

export interface StartBrandFactsInput {
  websiteUrl: string
  linkedInCompanyUrl: string
  keepEdited?: boolean
}

export interface StartBrandFactsResult {
  runId: string
}

const START_BRAND_FACTS_REASONS = [
  'brand_facts_run_in_progress',
  'invalid_linkedin_company_url',
  'invalid_website_url',
] as const

export const isBrandFactsRunInProgressReason = createReasonGuard([
  'brand_facts_run_in_progress',
] as const)

export const isInvalidLinkedInCompanyUrlReason = createReasonGuard([
  'invalid_linkedin_company_url',
] as const)

export const isInvalidWebsiteUrlReason = createReasonGuard(['invalid_website_url'] as const)

export const isStartBrandFactsReason = createReasonGuard(START_BRAND_FACTS_REASONS)

export const startBrandFacts = callable<StartBrandFactsInput, StartBrandFactsResult>(
  'startBrandFacts',
)

export interface ConfirmBrandFactsResult {
  facts: BrandFacts
}

const confirmBrandFactsCallable = callable<void, { facts: DocumentData }>('confirmBrandFacts')

/** Marks the sheet approved, finishing the first step of setup. */
export async function confirmBrandFacts(): Promise<ConfirmBrandFactsResult> {
  const result = await confirmBrandFactsCallable()
  return { facts: toBrandFacts(isRecord(result.facts) ? result.facts : {}) }
}
